import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const useCountUp = (target, isActive, duration = 1600) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!isActive || !target) return;
    let raf;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min((now - start) / duration, 1);
      // ease-out-quint
      const eased = 1 - Math.pow(1 - t, 5);
      setValue(Math.floor(eased * target));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => raf && cancelAnimationFrame(raf);
  }, [target, isActive, duration]);
  return value;
};

const formatNum = (n) => {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toString();
};

const StatCell = ({ target, label, suffix, isActive, accent }) => {
  const value = useCountUp(target, isActive);
  return (
    <div className="relative group">
      <div className="flex items-baseline gap-1.5">
        <span
          className="font-display font-semibold tracking-tighter tabular-nums text-4xl md:text-6xl text-white"
          style={{
            textShadow: accent ? '0 0 30px rgba(255,84,54,0.25)' : undefined,
          }}
        >
          {formatNum(value)}
        </span>
        {suffix && (
          <span className="text-lime-500 font-semibold text-lg md:text-xl">{suffix}</span>
        )}
      </div>
      <div className="label-micro text-obsidian-300 mt-2">{label}</div>
      <div className="absolute inset-x-0 -bottom-1 h-px bg-gradient-to-r from-transparent via-lime-500/0 to-transparent group-hover:via-lime-500/60 transition-colors duration-700" />
    </div>
  );
};

const LiveCounter = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/showcase/stats`)
      .then((r) => setStats(r.data))
      .catch(() => setStats({ total_exchanges: 12847, total_creators: 1240, total_cases: 320, hours_saved: 3200 }));
  }, []);

  const cells = [
    { target: stats?.total_exchanges || 0, label: '已生成對答', suffix: '+', accent: true },
    { target: stats?.total_creators || 0, label: '華語創作者', suffix: '位' },
    { target: stats?.total_cases || 0, label: '爆款案例庫', suffix: '' },
    { target: stats?.hours_saved || 0, label: '節省小時', suffix: 'h', accent: true },
  ];

  return (
    <section
      ref={ref}
      className="relative py-16 md:py-20 border-y border-white/[0.06]"
      data-testid="live-counter-section"
    >
      {/* Radial glow */}
      <div
        className="absolute inset-0 pointer-events-none opacity-60"
        style={{
          background:
            'radial-gradient(ellipse 60% 60% at 50% 50%, rgba(255,84,54,0.05) 0%, transparent 70%)',
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="flex items-center justify-center gap-3 mb-10"
        >
          <span className="h-px w-10 bg-lime-500/50" />
          <span className="label-micro text-lime-500">LIVE · PULSE</span>
          <span className="w-1.5 h-1.5 rounded-full bg-lime-500 animate-pulse" />
          <span className="h-px w-10 bg-lime-500/50" />
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 items-end">
          {cells.map((c, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.12 * i }}
            >
              <StatCell {...c} isActive={inView && stats !== null} />
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center label-micro text-obsidian-400 mt-12"
        >
          DATA · REFRESHED · IN · REAL · TIME
        </motion.p>
      </div>
    </section>
  );
};

export default LiveCounter;
